import {
  FieldError,
  FieldGroup,
  FieldHelper,
  type FieldProps,
  Input,
  Label,
  composeTailwindRenderProps,
} from '@vizit/atoms';
import { cx } from '@vizit/theme';
import React from 'react';
import { TextField as AriaTextField, type TextFieldProps as AriaTextFieldProps } from 'react-aria-components';

interface TextFieldProps extends FieldProps, AriaTextFieldProps {
  /**
   * The placeholder of the text field.
   *
   * @optional
   * @type string
   */
  placeholder?: string;
  /**
   * The secondary label of the text field.
   *
   * @optional
   * @type string
   */
  secondaryLabel?: string;
  /**
   * Icon to display at the start of the text field.
   *
   * @optional
   * @type React.ReactNode
   */
  startIcon?: React.ReactNode;
  /**
   * A ref used to connect form library to the input.
   *
   * @optional
   * @type React.Ref<HTMLInputElement>
   */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Additional props to pass to the input element.
   *
   * @optional
   * @type React.InputHTMLAttributes<HTMLInputElement>
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & {
    /** Stops 1P from trying to fill in the field */
    'data-1p-ignore'?: string;
    /** Stops LastPass from trying to fill in the field */
    'data-lpignore'?: string;
    /** Test ID for E2E tests */
    'data-testid'?: string;
  };
  /**
   * Additional className to pass to the FieldGroup wrapper.
   *
   * @optional
   * @type string
   */
  groupClassName?: string;
}

/**
 * The TextField component is used to create a text field.
 *
 * @param props - The props for the TextField component.
 * @example
 * <TextField
 *   label="Label"
 *   placeholder="Placeholder"
 *   secondaryLabel="Secondary Label"
 *   startIcon={<Icon />}
 * />
 */
function TextField(props: TextFieldProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    className,
    description,
    defaultValue,
    errorMessage,
    isDisabled,
    isInvalid,
    isRequired,
    label,
    placeholder,
    secondaryLabel,
    startIcon,
    maxLength,
    inputRef,
    inputProps,
    groupClassName,
    value: valueProp,
    ...other
  } = props;

  const { className: inputClassName, ...otherInputProps } = inputProps ?? {};

  return (
    <AriaTextField
      {...other}
      ref={ref}
      className={composeTailwindRenderProps(className, 'group flex min-w-[40px] flex-col gap-3')}
      defaultValue={defaultValue}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}
      <FieldGroup className={groupClassName} isDisabled={isDisabled} isInvalid={isInvalid}>
        {startIcon}
        <Input
          role="textbox"
          ref={inputRef}
          className={cx('h-full', inputClassName)}
          maxLength={maxLength}
          placeholder={placeholder}
          value={valueProp}
          {...otherInputProps}
        />
      </FieldGroup>
      {description && <FieldHelper>{description}</FieldHelper>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  );
}

const _TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(TextField);

export type { TextFieldProps };
export { _TextField as TextField };
